import axios from 'axios';
import {isEmpty} from 'lodash-es';
import {boundingExtent, Extent} from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import {ref} from 'vue';
import {defineStore} from 'pinia';

interface LocationResponse {
    Thoroughfarename: string;
    Housenumber: string;
    Municipality: string;
    FormattedAddress: string;

    BoundingBox: {
        LowerLeft: {
            Lat_WGS84: number;
            Lon_WGS84: number;
            X_Lambert72: number;
            Y_Lambert72: number;
            X_Lambert2008: number;
            Y_Lambert2008: number;
        };
        UpperRight: {
            Lat_WGS84: number;
            Lon_WGS84: number;
            X_Lambert72: number;
            Y_Lambert72: number;
            X_Lambert2008: number;
            Y_Lambert2008: number;
        };
    };
}

export const useKlMapSearchStore = defineStore('kl-map-search-store', () => {

    const lambert72 = 'EPSG:31370';
    const lambert2008 = 'EPSG:3812';

    const _config = {
        addressSuggestionUrl: `https://geo.api.vlaanderen.be/geolocation/Suggestion?q=:keyword&c=5`,
        addressSearchUrl: `https://geo.api.vlaanderen.be/geolocation/Location?q=:keyword&c=1`,
        parcelSuggectionUrl: `https://geo.api.vlaanderen.be/geolocation/perceel/suggestion?q=:keyword&c=5&data=adp&status=actual`,
        parcelSearchUrl: `https://geo.api.vlaanderen.be/capakey/v2/parcel/:keyword?geometry=full&data=adp&status=actual&srs=:projectionEPSG`,
        serviceProjection: 'EPSG:3812',
    };


    const fetching = ref(false);

    let _axiosClient = axios;
    const _getInjectedAxiosClient = () => {
        return _axiosClient;
    }

    // TODO: refactor using vi.mocking.. or injection..
    // hack to allow mocking in unit test
    const __setAxiosClient = (client) => {
        _axiosClient = client;
    }

    const fetchAddressSuggestions = async (input: string): Promise<string[]> => {
        if (!_config.addressSuggestionUrl) {
            return null;
        }
        if (isEmpty(input)) {
            return null;
        }

        fetching.value = true;
        const apiUrl = _config.addressSuggestionUrl.replace(":keyword", input.trim());
        const response = await _getInjectedAxiosClient().get(apiUrl);
        fetching.value = false;

        if (response && response.data && response.data.SuggestionResult) {
            return response.data.SuggestionResult;
        }

        return null;
    }

    const fetchAddressExtent = async (address: string, projection: string): Promise<Extent> => {
        if (!_config.addressSearchUrl) {
            return null;
        }
        if (isEmpty(address)) {
            return null;
        }

        fetching.value = true;
        const apiUrl = _config.addressSearchUrl.replace(":keyword", address);
        const response = await _getInjectedAxiosClient().get(apiUrl);
        fetching.value = false;

        if (response?.data?.LocationResult) {
            const locationResult: LocationResponse = response.data.LocationResult[0];
            const coordinates = locationResult.BoundingBox;

            let bbox: number[][] = null;
            if (projection === lambert2008) {
                bbox = [
                    [coordinates.LowerLeft.X_Lambert2008, coordinates.LowerLeft.Y_Lambert2008],
                    [coordinates.UpperRight.X_Lambert2008, coordinates.UpperRight.Y_Lambert2008]
                ];
            }
            else if (projection === lambert72) {
                bbox = [
                    [coordinates.LowerLeft.X_Lambert72, coordinates.LowerLeft.Y_Lambert72],
                    [coordinates.UpperRight.X_Lambert72, coordinates.UpperRight.Y_Lambert72]
                ];
            }
            else {
                throw new Error(`fetchAddressExtent: unsupported projection '${projection}'`);
            }

            const extent: Extent = boundingExtent(bbox);
            return extent;
        }
        return null;
    }

    const fetchParcelSuggestions = async (input: string): Promise<string[]> => {
        if (!_config.parcelSuggectionUrl) {
            return null;
        }
        if (isEmpty(input)) {
            return null;
        }

        fetching.value = true;
        const apiUrl = _config.parcelSuggectionUrl.replace(":keyword", input.trim());
        const response = await _getInjectedAxiosClient().get(apiUrl);
        fetching.value = false;

        if (response && response.data && response.data.SuggestionResult) {
            return response.data.SuggestionResult;
        }

        return null;
    }

    const fetchParcelExtent = async (capakey: string, projection: string): Promise<Extent> => {
        if (!_config.parcelSearchUrl) {
            return null;
        }
        if (isEmpty(capakey)) {
            return null;
        }

        fetching.value = true;
        let apiUrl = _config.parcelSearchUrl.replace(":keyword", capakey);
        if (projection === lambert2008) {
            apiUrl = apiUrl.replace(':projectionEPSG', '3812');
        }
        else if (projection === lambert72) {
            apiUrl = apiUrl.replace(':projectionEPSG', '31370');
        }
        else {
            throw new Error(`fetchParcelExtent: unsupported projection '${projection}'`);
        }

        const response = await _getInjectedAxiosClient().get(apiUrl);
        fetching.value = false;

        if (response?.data?.geometry) {
            const bbox = new GeoJSON().readFeatures(response.data.geometry.boundingBox)
            const extent = bbox[0].getGeometry().getExtent();
            return extent;
        }
        return null;
    }

    return {
        fetching,
        fetchAddressSuggestions,
        fetchAddressExtent,
        fetchParcelSuggestions,
        fetchParcelExtent,
        __setAxiosClient,
    }
});
